import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/hooks';
import {
  ISO_STANDARDS,
  ISO_STANDARD_DESCRIPTIONS,
  ISO_STANDARD_LABELS,
  ISO_STANDARD_SHORT_LABELS,
  type IsoStandard,
} from '../../../constants/isoStandards';
import {
  loadOrganizationStandards,
  saveOrganizationStandards,
  selectEnabledStandards,
  selectOrganizationStandardsSaveStatus,
  selectOrganizationStandardsStatus,
} from '../organizationStandardsSlice';
import { showToast } from '../../../components/ui/toast.utils';
import { extractApiMessage } from '../../../lib/apiError';

interface OrganizationStandardsSelectorProps {
  canEdit: boolean;
  variant?: 'page' | 'inline';
}

export function OrganizationStandardsSelector({
  canEdit,
  variant = 'page',
}: OrganizationStandardsSelectorProps) {
  const dispatch = useAppDispatch();
  const enabledStandards = useSelector(selectEnabledStandards);
  const status = useSelector(selectOrganizationStandardsStatus);
  const saveStatus = useSelector(selectOrganizationStandardsSaveStatus);
  const [selected, setSelected] = useState<IsoStandard[]>([]);

  useEffect(() => {
    if (status === 'idle' || status === 'failed') {
      void dispatch(loadOrganizationStandards());
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (enabledStandards) {
      setSelected(enabledStandards);
    }
  }, [enabledStandards]);

  const toggleStandard = (standard: IsoStandard) => {
    if (!canEdit) return;
    setSelected((prev) => {
      if (prev.includes(standard)) {
        if (prev.length <= 1) {
          showToast('Debe permanecer al menos una norma activa.', 'warning');
          return prev;
        }
        return prev.filter((s) => s !== standard);
      }
      return [...prev, standard];
    });
  };

  const handleSave = async () => {
    if (!canEdit || selected.length === 0) {
      showToast('Debe permanecer al menos una norma activa.', 'warning');
      return;
    }
    try {
      await dispatch(saveOrganizationStandards(selected)).unwrap();
      showToast('Normas actualizadas. El menú y los módulos se ajustan al guardar.', 'success');
    } catch (err) {
      showToast(extractApiMessage(err, 'No se pudieron guardar las normas.'), 'error');
    }
  };

  if (status === 'loading' || status === 'idle') {
    return (
      <p className="text-sm text-muted-foreground">Cargando normas habilitadas…</p>
    );
  }

  if (status === 'failed') {
    return (
      <p className="text-sm text-destructive">No se pudo cargar la configuración de normas.</p>
    );
  }

  const gridClass =
    variant === 'inline'
      ? 'grid gap-3 sm:grid-cols-2'
      : 'grid w-full gap-3 sm:grid-cols-2';

  return (
    <div
      className={
        variant === 'inline'
          ? 'space-y-4'
          : 'mx-auto flex max-w-3xl flex-col items-center space-y-6 text-center'
      }
    >
      {variant === 'page' ? (
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Normas ISO de la organización</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl mx-auto">
            Elegí con qué normas trabaja esta organización. Al desactivar una norma se ocultan sus
            módulos y el Centro de implementación correspondiente; los datos se conservan.
          </p>
        </div>
      ) : (
        <div>
          <h2 className="text-base font-semibold">Normas ISO de esta organización</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Solo el consultor Microsyst (superadmin) puede cambiar qué normas están activas.
          </p>
        </div>
      )}

      <div className={gridClass}>
        {ISO_STANDARDS.map((standard) => {
          const active = selected.includes(standard);
          return (
            <button
              key={standard}
              type="button"
              disabled={!canEdit}
              onClick={() => toggleStandard(standard)}
              className={`rounded-lg border p-4 text-left transition ${
                active
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                  : 'border-border bg-card opacity-75 hover:border-muted-foreground/30'
              } ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
              aria-pressed={active}
              data-testid={`standard-toggle-${standard}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold">
                  {variant === 'inline'
                    ? ISO_STANDARD_SHORT_LABELS[standard]
                    : ISO_STANDARD_LABELS[standard]}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                    active
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                {ISO_STANDARD_DESCRIPTIONS[standard]}
              </p>
            </button>
          );
        })}
      </div>

      {canEdit ? (
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveStatus === 'loading'}
          className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {saveStatus === 'loading' ? 'Guardando…' : 'Guardar configuración'}
        </button>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          Configuración de solo lectura. Contactá al consultor Microsyst para modificar las normas.
        </p>
      )}
    </div>
  );
}
